import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'
import { Channel } from 'stream-chat'
import { StreamChat as StreamClient } from 'stream-chat'
import { useDispatch, useSelector } from 'react-redux'
import { StackActions, useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import moment from 'moment'
import { v4 as uuidV4 } from 'uuid'
import StreamChat from '@services/StreamChat'
import { userSelector } from '@selectors/app'
import { Storage, useDispatchAsync, useSnack } from '@utils'
import { ChatbotConversation, Token } from '@src/types'
import { Bugsnag, ErrorMessages } from '@config'
import { CHANNEL_ERROR } from '@screens/Nora/constants'
import { AppStackParamList } from '@src/navigation/types'
import { NoraUploadedAttachmentPreview } from '@src/screens/Nora/hooks/useNoraAttachmentUpload'
import { NoraVoiceModeSource } from '@src/screens/Nora/constants'
import {
  AI_PROCESSING_CONSENT_ERROR,
  useGetRequiredAiProcessingPolicy,
} from '@src/screens/PolicyConsents/hooks/useAiProcessingConsentGate'

interface OnAskProps {
  initialPrompt?: string
  prompt?: string
  quickPromptId?: string
  reportId?: string
  initialVoiceModeEnabled?: boolean
  initialVoiceModeSource?: NoraVoiceModeSource
  userFileIds?: string[]
  userFilePreviews?: NoraUploadedAttachmentPreview[]
}

type ActionTunnelDraftUpdater = (sourceDraft: unknown) => unknown

interface StreamChatContextValue {
  client: StreamClient
  channel: Channel | null
  shouldReloadConversations: boolean
  setShouldReloadConversations: (value: boolean) => void
  openConversation: (
    conversation: ChatbotConversation,
    options?: { replace?: boolean },
  ) => Promise<void>
  connectToConversation: (providerId: string) => Promise<void>
  // Resolves to true only when a conversation was started
  onAsk: (props: OnAskProps) => Promise<boolean>
  ensureAiProcessingConsent: () => Promise<boolean>
  saveCurrentActionTunnelDraft: (params: {
    draftUpdater: ActionTunnelDraftUpdater
    messageId?: string
  }) => Promise<void>
  closeChannel: () => void
}

const NoraStreamChatContext = createContext<StreamChatContextValue | null>(null)

export const NoraStreamChatProvider = ({ children }: { children: ReactNode }) => {
  const client = StreamChat.getClient()
  const user = useSelector(userSelector)
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const showSnack = useSnack()
  const [channel, setChannel] = useState<Channel | null>(null)
  const [shouldReloadConversations, setShouldReloadConversations] = useState(false)
  const connectionPromiseRef = useRef<Promise<void> | null>(null)
  const { getRequiredAiProcessingPolicy } = useGetRequiredAiProcessingPolicy()

  // Nora can't be used until the AI processing consent is accepted. Returns false (and opens the
  // consent screen) when consent is still required, or when it can't be verified (fails closed).
  const ensureAiProcessingConsent = useCallback(async () => {
    try {
      const policy = await getRequiredAiProcessingPolicy()
      if (policy) {
        navigation.navigate('PolicyConsent', { policy })
        return false
      }
      return true
    } catch {
      showSnack(AI_PROCESSING_CONSENT_ERROR, null, 'error')
      return false
    }
  }, [getRequiredAiProcessingPolicy, navigation, showSnack])

  const ensureConnected = useCallback(async () => {
    if (!user?.id) {
      return
    }

    // Skip if already connected to the same user
    if (client.userID === user.id) {
      return
    }

    if (connectionPromiseRef.current) {
      await connectionPromiseRef.current
      return
    }

    const connectUser = async () => {
      const storageTokenKey = `${Storage.STREAM_CHAT_TOKEN_KEY}_${user.id}`
      const tokenExpireAtKey = `${Storage.STREAM_CHAT_TOKEN_EXPIRE_AT_KEY}_${user.id}`

      const tokenProvider = async () => {
        const storageToken = Storage.get<string>(storageTokenKey)
        const tokenExpireAt = Storage.get(tokenExpireAtKey)

        if (storageToken && tokenExpireAt && moment().isBefore(moment(tokenExpireAt))) {
          return storageToken
        }

        const newToken = await dispatchAsync<Token>({ type: 'chatbot/createChatbotToken' })
        const token = newToken.value
        Storage.set(storageTokenKey, token)
        // Stream tokens are valid for 60 minutes (leaving a buffer)
        Storage.set(tokenExpireAtKey, moment().add(50, 'minutes'))
        return token
      }

      try {
        await client.connectUser(
          {
            id: user.id,
            name: user.fullName,
          },
          tokenProvider,
        )
      } catch (error: any) {
        Bugsnag.notify(`StreamChat - connectUser error: ${error.message}`, (event) => {
          // eslint-disable-next-line no-param-reassign
          event.severity = 'info'
        })
        return
      }

      dispatch({ type: 'chatbot/setLastShownTimestamp', payload: moment().toISOString() })
    }

    connectionPromiseRef.current = connectUser()

    try {
      await connectionPromiseRef.current
    } finally {
      connectionPromiseRef.current = null
    }
  }, [user?.id, user?.fullName, client, dispatchAsync, dispatch])

  useEffect(() => {
    return () => {
      if (client.userID) {
        client.disconnectUser().then(() => console.log('Nora stream chat connection closed'))
      }
      connectionPromiseRef.current = null
    }
  }, [client])

  const createChannel = useCallback(
    async (idSuffix: string, name?: string, quickPromptId?: string, reportId?: string) => {
      if (!user) {
        return null
      }

      await ensureConnected()

      const channel = client.channel('chatbot-web', `${user.id}-${idSuffix}`, {
        name,
        // @ts-ignore
        kind: reportId ? 'report' : 'conversation',
        quick_prompt_id: quickPromptId,
        report_id: reportId,
        members: [user.id],
      })

      await channel.watch()

      return channel
    },
    [client, user, ensureConnected],
  )

  const connectToChannel = useCallback(
    async (providerId: string) => {
      if (!user) {
        return null
      }

      await ensureConnected()

      const newChannel = client.channel('chatbot-web', providerId, {
        members: [user.id],
      })

      try {
        await newChannel.watch()
      } catch (error: any) {
        Bugsnag.notify(`StreamChat - watch error: ${error.message}`, (event) => {
          // eslint-disable-next-line no-param-reassign
          event.severity = 'info'
        })
        return null
      }

      return newChannel
    },
    [client, user, ensureConnected],
  )

  const openConversation = useCallback(
    async (conversation: ChatbotConversation, options?: { replace?: boolean }) => {
      if (!(await ensureAiProcessingConsent())) {
        return
      }

      const newChannel = await connectToChannel(conversation.providerId)

      if (newChannel) {
        setChannel(newChannel)
        if (options?.replace) {
          navigation.dispatch(StackActions.replace('NoraConversationScreen', { conversation }))
        } else {
          navigation.navigate('NoraConversationScreen', { conversation })
        }
      } else {
        showSnack(CHANNEL_ERROR, null, 'error')
      }
    },
    [connectToChannel, ensureAiProcessingConsent, navigation, showSnack],
  )

  const connectToConversation = useCallback(
    async (providerId: string) => {
      const newChannel = await connectToChannel(providerId)

      if (newChannel) {
        setChannel(newChannel)
      } else {
        showSnack(CHANNEL_ERROR, null, 'error')
      }
    },
    [connectToChannel, showSnack],
  )

  const onAsk = useCallback(
    async ({
      initialPrompt,
      prompt,
      quickPromptId,
      reportId,
      initialVoiceModeEnabled,
      initialVoiceModeSource,
      userFileIds,
      userFilePreviews,
    }: OnAskProps) => {
      const trimmedPrompt = prompt?.trim()
      const trimmedInitialPrompt = initialPrompt?.trim()
      // Single effective prompt so the channel name and the navigation param never diverge.
      const effectivePrompt = trimmedPrompt || trimmedInitialPrompt
      const hasPrompt = !!trimmedPrompt
      const hasInitialPrompt = !!trimmedInitialPrompt
      const hasUserFileIds = !!userFileIds?.length

      if (!hasPrompt && !hasInitialPrompt && !quickPromptId && !reportId && !hasUserFileIds) {
        return false
      }

      if (!(await ensureAiProcessingConsent())) {
        return false
      }

      try {
        const newChannel = await createChannel(uuidV4(), effectivePrompt, quickPromptId, reportId)
        if (newChannel) {
          setChannel(newChannel)
          if (hasPrompt || hasUserFileIds) {
            newChannel.sendMessage({
              ...(hasPrompt ? { text: trimmedPrompt } : {}),
              ...(hasUserFileIds ? { user_file_ids: userFileIds } : {}),
              ...(hasUserFileIds ? { user_file_previews: userFilePreviews } : {}),
            })
          }
          setShouldReloadConversations(true)
          navigation.navigate('NoraConversationScreen', {
            initialPrompt: hasPrompt || hasUserFileIds ? undefined : effectivePrompt,
            initialVoiceModeEnabled,
            ...(initialVoiceModeSource ? { initialVoiceModeSource } : {}),
          })
          return true
        }
      } catch (error: any) {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
      }
      return false
    },
    [createChannel, ensureAiProcessingConsent, navigation, showSnack],
  )

  const saveCurrentActionTunnelDraft = useCallback(
    async ({
      draftUpdater,
      messageId,
    }: {
      draftUpdater: ActionTunnelDraftUpdater
      messageId?: string
    }) => {
      const providerId = channel?.id

      if (!providerId) {
        throw new Error('No active conversation to update.')
      }

      const message = channel.state.messages.find((item) => item.id === messageId)
      const sourceDraft = message?.active_action_tunnel?.draft

      if (!messageId || sourceDraft === undefined) {
        throw new Error('No active action tunnel message draft to update.')
      }

      try {
        const nextDraft = draftUpdater(sourceDraft)

        await dispatchAsync({
          type: 'meals/updateChatbotConversationTunnelDraft',
          payload: {
            providerId,
            draft: nextDraft,
            streamChatMessageId: messageId,
          },
        })
      } catch (error: any) {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
        throw error
      }
    },
    [channel, dispatchAsync, showSnack],
  )

  const closeChannel = useCallback(() => {
    if (channel) {
      channel.stopWatching()
    }
    setChannel(null)
  }, [channel])

  const value = useMemo(
    () => ({
      client,
      channel,
      shouldReloadConversations,
      setShouldReloadConversations,
      openConversation,
      connectToConversation,
      onAsk,
      ensureAiProcessingConsent,
      saveCurrentActionTunnelDraft,
      closeChannel,
    }),
    [
      channel,
      client,
      closeChannel,
      ensureAiProcessingConsent,
      onAsk,
      openConversation,
      saveCurrentActionTunnelDraft,
      connectToConversation,
      shouldReloadConversations,
    ],
  )

  return <NoraStreamChatContext.Provider value={value}>{children}</NoraStreamChatContext.Provider>
}

export const useNoraStreamChat = () => {
  const context = useContext(NoraStreamChatContext)
  if (!context) {
    throw new Error('useNoraStreamChat must be used within NoraStreamChatProvider')
  }
  return context
}
